/**
 * `execute-sharepoint-ingest` — CLM-50. The ninth endpoint, and the execute half
 * of the pair `plan-sharepoint-ingest` opened.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ## The request carries coordinates, never facts
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `approved` is a list of `(drive_id, item_id, etag)` triples and **nothing
 * else**. No `party_key`, no `sow_number`, no `contract_key`. That is not
 * minimalism: the server re-crawls the folder, re-runs `planIngest`, and executes
 * only the triples that appear in the fresh plan's `to_ingest` — so a party or a
 * SOW number chosen by a client is not merely rejected, it is *unrepresentable*
 * (CLAUDE.md rule 9). A triple absent from the fresh plan comes back in `skipped`
 * carrying the planner's own reason, which is also how a document that changed
 * between the plan and the approval is reported rather than silently ingested
 * under a stale eTag.
 *
 * The eTag is the reason a re-plan is safe rather than merely defensive: the
 * triple the admin approved names a *version*, so a document re-signed in the
 * meantime no longer matches and lands in `skipped` instead of being written
 * against an approval nobody gave for it.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ## `202`, not `200`
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Same wording as `upload` in the registry: *the rows exist, the facts do not*.
 * This call writes `clm.contract`, `clm.contract_party` and
 * `clm.sharepoint_ingest_ledger`, and it stops — `processing_status` is left at
 * `PendingExtraction` (or `manual_entry_pending` for a restricted party) for the
 * pipeline to pick up. Nothing is converted, pseudonymised or extracted here, so
 * rule 2's order — intake → convert → pseudonymise → extract — holds because
 * intake genuinely ends at this response.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ## Three outcome lists, not one
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `ingested`, `skipped` and `failed` are separate arrays because the operator
 * actions differ and a single status field would collapse them. `skipped` is *the
 * planner disagreed* — already ingested, or no longer eligible — and needs no
 * action. `failed` is *this document could not be written* and names which of the
 * four ways it failed. A `failed` entry never leaves a partial row behind: each
 * document is one transaction, so a failure rolls its own writes back and the
 * next document still commits.
 *
 * `detail` on a failure is operator-facing prose. It never carries a byte of the
 * document, a hash of one, or a pre-authenticated URL — the download exists to
 * verify the bytes and hand them nowhere (IR-05).
 *
 * CLM-122: a Type pending row is `ingested` like any other, filed `OTHER` with
 * `contract_type_confirmation 'pending'`, and `totals.type_pending` counts them.
 */
import { z } from 'zod';
import { ContractKeySchema, HttpsUrlSchema, OpaqueIdSchema, PartyKeySchema } from '../primitives.js';
import { AuditReceiptSchema } from '../provenance.js';
import { ContractTypeNameEvidenceSchema, ProcessingStatusRatifiedSchema } from '../vocabulary.js';
import { TAGS_PER_CONTRACT_MAX, TagSchema } from './contract-tags.js';
import { ContractTypeSourceSchema, ETagSchema, GraphItemIdSchema, IngestContractTypeSchema, IngestSkipReasonSchema, MAX_PLAN_MAX_DOCUMENTS, PartyKeySourceSchema, PlanStateSchema, } from './plan-sharepoint-ingest.js';
/* -------------------------------------------------------------------------- */
/* Request                                                                     */
/* -------------------------------------------------------------------------- */
/**
 * One document the admin approved, addressed the way the plan addressed it.
 *
 * `.strict()` so a `party_key` or `sow_number` smuggled onto the object is a
 * validation error rather than a field the server quietly ignores. The difference
 * matters: an ignored field looks accepted to whoever sent it.
 */
export const ApprovedIngestItemSchema = z
    .object({
    drive_id: GraphItemIdSchema,
    item_id: GraphItemIdSchema,
    /** Verbatim, quotes included. The *version* the admin approved. */
    etag: ETagSchema,
    /** CLM-71. Validated server-side by the SOW shape rules and upper-cased; the client's casing is not trusted. */
    sow_number_override: z.string().min(1).max(32).optional(),
    /** CLM-71. Must resolve to a party inside the caller's scope, or the item fails `override_rejected`. */
    party_key_override: PartyKeySchema.optional(),
    /* CLM-118 (R-118-18, B9): no type override — nobody picks a type. `.strict()` makes one a 400. */
    /** CLM-77. Normalised tags applied in the same transaction as the contract row; not a correction, never re-plans. */
    tags: z.array(TagSchema).max(TAGS_PER_CONTRACT_MAX).optional(),
    /**
     * CLM-126 (R-126-12) — present when the admin accepted a nearest-match
     * suggestion offered on this document's skipped row. Credited to the
     * `NEAREST_MATCH_SOURCE_COMPONENT` actor only when `party_key_override`
     * equals this `party_key` — a reviewer who then picks something else
     * instead is an ordinary manual correction (D-88-4's precedence: the
     * human's final choice is the stronger provenance).
     */
    suggestion_accepted: z.object({ party_key: PartyKeySchema, score: z.number().min(0).max(1) }).optional(),
})
    .strict();
export const ExecuteSharepointIngestRequestSchema = z
    .object({
    /** The same pasted sharing link the plan was built from. Re-crawled here. */
    folder_link: HttpsUrlSchema,
    /**
     * The approved subset of a plan's `to_ingest`, as coordinates only.
     *
     * `min(1)` because an execute with nothing approved is a mistake, not a
     * no-op worth an audit row. The cap is `plan-sharepoint-ingest`'s own, so an
     * approval cannot exceed the largest plan that could have produced it.
     */
    approved: z.array(ApprovedIngestItemSchema).min(1).max(MAX_PLAN_MAX_DOCUMENTS),
    /** Cap on the re-crawl, matching the plan call that produced `approved`. */
    max_documents: z.number().int().min(1).max(MAX_PLAN_MAX_DOCUMENTS).optional(),
    /**
     * Mirrors the plan call that produced `approved`; the re-crawl must see the
     * same candidates or an unsigned approval is reported `skipped`. CLM-67.
     */
    include_unsigned_pdfs: z.boolean().optional(),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Response                                                                    */
/* -------------------------------------------------------------------------- */
/** Why one approved document could not be written. */
export const INGEST_FAILURE_REASONS = [
    /** Graph would not give up the bytes — 403, throttled past the budget, 5xx. */
    'download_failed',
    /** The eTag moved between the re-plan and the download. A later run picks it up. */
    'version_changed',
    /** Not a PDF, or over the byte cap. Fails closed — see the CLM-50 report. */
    'document_unusable',
    /** The transaction rolled back — most often a party outside the caller's scope. */
    'write_failed',
    /**
     * CLM-71 (D-71-6). The item carried a manual correction the server refused —
     * party outside scope, unusable number, number held by another party, or a row
     * the planner still cannot resolve. `detail` names the sub-reason. A failure,
     * not a skip: the admin acted and must see the refusal.
     */
    'override_rejected',
    /**
     * CLM-110 (R-110-10). The minted key is already held — by a contract outside the
     * caller's scope (the mint's `max()` is scoped), or by a writer that skipped the
     * per-party lock. Never reported as `already_ingested`: the document is not filed.
     */
    'key_collision',
    /** CLM-110 (R-110-10). The party's four-digit series is used up (above 9999); never truncated. */
    'key_sequence_exhausted',
];
export const IngestFailureReasonSchema = z.enum(INGEST_FAILURE_REASONS);
/**
 * A document that became a contract.
 *
 * `intake_path` is the literal `'Backfill'`: this endpoint is the only producer
 * of that value, and a client reading the response should not have to ask which
 * path a backfill took.
 *
 * `ingest_audit_id` is the `clm.audit_log` row `tg_contract_audit` wrote for the
 * `INSERT`, which is also what `clm.sharepoint_ingest_ledger.ingest_audit_id`
 * points at. It is the evidence handle for this ingest (NFR-07, rule 7) — quoted
 * back so an operator can find the row without joining on a correlation id.
 */
export const IngestedDocumentSchema = z.object({
    drive_id: GraphItemIdSchema,
    item_id: GraphItemIdSchema,
    etag: ETagSchema,
    contract_key: ContractKeySchema,
    /** Re-derived server-side from the folder path. Never echoed from the request. */
    party_key: PartyKeySchema,
    /** IR-01 — reused from the source system, never minted. CLM-76: `null` for MSA / NDA / OTHER. */
    sow_number: z.string().min(1).max(32).nullable(),
    /**
     * G26 (a) — whether the number was registered or unregistered at ingest. No
     * contract column records this (D-63-2); the receipt and the log line are the
     * evidence, so Part D keeps the receipt JSON.
     */
    plan_state: PlanStateSchema,
    /**
     * D-67-4 — no contract column records this; the receipt and the log line are
     * the evidence, as for `plan_state`. `false` only under `include_unsigned_pdfs`.
     */
    signed_marker: z.boolean(),
    /**
     * CLM-71 (D-71-3). `manual` when the admin set the party at plan review; the
     * receipt, the log line and the audit row's `source_component` are the evidence
     * (D-71-1) — no contract column records it.
     */
    party_key_source: PartyKeySourceSchema,
    /** CLM-71. The number the admin typed (upper-cased, validated); `null` when derived. */
    sow_number_override: z.string().min(1).max(32).nullable(),
    /** CLM-71. The party the admin selected; `null` when derived. */
    party_key_override: PartyKeySchema.nullable(),
    /** CLM-76. The type the contract was filed as. */
    contract_type: IngestContractTypeSchema,
    /** CLM-76, CLM-118 — always `derived`: the planner's file-name-first rule; nobody picks a type (R-118-18). */
    contract_type_source: ContractTypeSourceSchema,
    /**
     * CLM-118 (R-118-3) — what the file-name rule found: `typed`, `conflicting`
     * (never confirmed by agreement; the AI's type replaces it once read), or
     * `untyped` for a row typed only by the reviewer's number.
     */
    contract_type_name_evidence: ContractTypeNameEvidenceSchema,
    /**
     * CLM-110 (R-110-23) — `provisional` at intake: the file-name type waits for
     * the AI's reading (CLM-118: confirmed when they agree) or an Admin. CLM-122
     * (R-122-13): `pending` for a Type pending row — the `OTHER` placeholder the AI
     * types after filing. Bulk ingest files no amendment and nothing settled, so
     * `confirmed` and `auto_confirmed` never appear here.
     */
    contract_type_confirmation: z.enum(['provisional', 'pending']),
    /** CLM-77. The tags stored with the row (sorted); `[]` when none were sent. */
    tags: z.array(TagSchema),
    /**
     * `PendingExtraction`, or `manual_entry_pending` when the party is
     * `processing_restricted` (§4C, rule 4). Ratified values only: intake never
     * writes one of the provisional pipeline states.
     */
    processing_status: ProcessingStatusRatifiedSchema,
    intake_path: z.literal('Backfill'),
    ingest_audit_id: OpaqueIdSchema,
});
/**
 * An approved document the server declined to ingest.
 *
 * `reason` reuses the **plan's** vocabulary rather than inventing a second one,
 * because every member of it is a planner verdict: `already_ingested` for a
 * re-run, and the resolution reasons for a triple that no longer appears in
 * `to_ingest`.
 */
export const SkippedIngestSchema = z.object({
    drive_id: GraphItemIdSchema,
    item_id: GraphItemIdSchema,
    /** `null` when the fresh crawl found the item without an eTag (`no_etag`). */
    etag: ETagSchema.nullable(),
    reason: IngestSkipReasonSchema,
});
export const FailedIngestSchema = z.object({
    drive_id: GraphItemIdSchema,
    item_id: GraphItemIdSchema,
    /**
     * Operator-facing prose. Never a document byte, never a hash of one, never a
     * pre-authenticated URL.
     */
    detail: z.string().min(1).max(1000),
    reason: IngestFailureReasonSchema,
});
export const ExecuteIngestTotalsSchema = z.object({
    /** How many triples the request carried. The denominator for the other three. */
    approved: z.number().int().min(0),
    ingested: z.number().int().min(0),
    skipped: z.number().int().min(0),
    failed: z.number().int().min(0),
    /** CLM-122 (R-122-13) — the `ingested` rows filed as Type pending (`contract_type_confirmation 'pending'`). */
    type_pending: z.number().int().min(0),
});
export const ExecuteSharepointIngestResponseSchema = z.object({
    ingested: z.array(IngestedDocumentSchema),
    skipped: z.array(SkippedIngestSchema),
    failed: z.array(FailedIngestSchema),
    totals: ExecuteIngestTotalsSchema,
    /**
     * NFR-07. This call both enumerated the folder structure of cloudEQ's client
     * contracts *and* created contracts, so the receipt is not optional decoration.
     */
    audit: AuditReceiptSchema,
}).superRefine((value, ctx) => {
    /* CLM-122 (R-122-13) — a Type pending row is exactly the OTHER placeholder: no SOW number, no trusted name type. */
    value.ingested.forEach((row, index) => {
        if ((row.contract_type_confirmation === 'pending') !== (row.contract_type === 'OTHER')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['ingested', index, 'contract_type_confirmation'],
                message: 'a bulk row is filed pending exactly when it is the OTHER placeholder (CLM-122)',
            });
        }
        if (row.contract_type_confirmation === 'pending') {
            if (row.sow_number !== null) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ingested', index, 'sow_number'], message: 'a Type pending row carries no SOW number' });
            }
            if (row.contract_type_name_evidence === 'typed') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['ingested', index, 'contract_type_name_evidence'],
                    message: 'a Type pending row has evidence untyped or conflicting',
                });
            }
        }
    });
    const pending = value.ingested.filter((row) => row.contract_type_confirmation === 'pending').length;
    if (value.totals.type_pending !== pending) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['totals', 'type_pending'], message: 'totals.type_pending counts the pending rows' });
    }
});
//# sourceMappingURL=execute-sharepoint-ingest.js.map